import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDarkMode } from '../hooks/useDarkMode';
import { signOut } from '../services/auth';
import useStore from '../store';
import DeleteAccountSection from '../components/DeleteAccountSection';
import { openExternal } from '../services/openLink';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function displayName(user) {
  const loginId = user?.signInDetails?.loginId;
  if (loginId) return loginId;
  const u = user?.username;
  // Bare Cognito sub UUIDs surface for federated sign-ins (e.g. Apple)
  // before the idToken email claim is unpacked. Hide them — "User" is
  // far better than a 36-char hex string in the Account card.
  if (u && !UUID_RE.test(u)) return u;
  return 'User';
}

export default function Settings() {
  const navigate = useNavigate();
  const { isDark, toggle } = useDarkMode();
  const setUser = useStore((s) => s.setUser);
  const user = useStore((s) => s.user);
  const clearSignedInData = useStore((s) => s.clearSignedInData);
  const { subscription, subscriptionLoading, fetchSubscription } = useStore();
  const isPremium = useStore((s) => s.isPremium);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  async function handleSignOut() {
    try {
      await signOut();
      // Wipe locally-cached data before dropping the user reference so the next
      // person on a shared device can't see the previous user's habits.
      // Best-effort — a failure here must not block sign-out.
      try { await clearSignedInData?.(); } catch { /* ignore */ }
      setUser(null);
      navigate('/login');
    } catch {
      toast.error('Failed to sign out');
    }
  }

  const isPremiumUser = isPremium();

  function getSubscriptionLabel() {
    if (subscriptionLoading) return 'Loading...';
    if (!subscription || subscription.tier === 'free') {
      if (subscription?.premiumPreview?.active) {
        return 'Free Plan (Premium Preview)';
      }
      return 'Free Plan';
    }
    if (subscription.isTrialing) return 'Premium Plan (Trial)';
    return 'Premium Plan';
  }

  function getSubscriptionSubtext() {
    if (subscriptionLoading) return '';
    if (isPremiumUser && subscription?.periodEnd) {
      const verb = subscription.cancelAtPeriodEnd ? 'Expires' : 'Renews';
      const date = new Date(subscription.periodEnd).toLocaleDateString();
      return `${verb} on ${date}`;
    }
    if (subscription?.premiumPreview?.active) {
      const daysLeft = Math.max(0, Math.ceil(
        (new Date(subscription.premiumPreview.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      ));
      return `Preview ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
    }
    return 'Upgrade to Premium for unlimited habits';
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center gap-3 shadow-sm safe-area-top">
        <button
          onClick={() => navigate('/')}
          aria-label="Back to dashboard"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Settings</h1>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-2">
        {/* Account */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Account
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">Signed in as</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {displayName(user)}
            </p>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Appearance
            </p>
          </div>
          <button
            onClick={toggle}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{isDark ? '🌙' : '☀️'}</span>
              <span className="text-gray-900 dark:text-white font-medium">Dark Mode</span>
            </div>
            <div
              className={`w-11 h-6 rounded-full relative transition-colors ${
                isDark ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  isDark ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </div>
          </button>
        </div>

        {/* Subscription */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Subscription
            </p>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl">✨</span>
                <div>
                  <span className="text-gray-900 dark:text-white font-medium block">
                    {getSubscriptionLabel()}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {getSubscriptionSubtext()}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              {!isPremiumUser && (
                <button
                  onClick={() => navigate('/premium')}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Upgrade to Premium
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Legal */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Legal
            </p>
          </div>
          <button
            type="button"
            onClick={() => openExternal('/privacy.html')}
            className="block w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
          >
            Privacy Policy
          </button>
          <button
            type="button"
            onClick={() => openExternal('/terms.html')}
            className="block w-full text-left px-4 py-3 border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
          >
            Terms of Use (EULA)
          </button>
        </div>

        {/* Delete Account — Apple Guideline 5.1.1(v) */}
        <DeleteAccountSection />

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className="w-full mt-4 py-3 text-red-500 dark:text-red-400 font-medium bg-white dark:bg-gray-800 rounded-2xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}
